import React from 'react';

import Button from 'components/Button';
import { useAppLanguage } from 'hooks';
import appUrls from 'helpers/app-urls';

import { SSOAuthProviderKey } from 'entities/sso';
import { LoginMethodOption } from 'pages/Profile/ProfileLoginMethod';

// import messages from 'pages/Profile/messages';
import Modal from 'components/Modal';
import { toSentenceCase } from 'utils/casing';
import { loginWith } from 'utils/firebase';
import { useDispatch } from 'react-redux';
import { loginWithProviderAction } from 'store/auth/auth-actions';
import messages from './messages';

const cdnUrl = appUrls?.getCDNUrl();
const imgUser = `${cdnUrl}/assets/auth/feature-icon-user.png`;

const imgAppleFeatureIcon = `${cdnUrl}/assets/auth/feature-icon-apple.png`;
const imgAppleIcon = `${cdnUrl}/assets/logos/apple-logo-white.svg`;
// const imgAppleFeatureIconChecked = `${appUrls?.getCDNUrl()}/assets/auth/feature-icon-apple-checked.png`;
const imgGoogleFeatureIcon = `${appUrls?.getCDNUrl()}/assets/auth/feature-icon-google.png`;
const imgGoogleIcon = `${cdnUrl}/assets/logos/google-logo-white.svg`;
// const imgGoogleFeatureIconChecked = `${appUrls?.getCDNUrl()}/assets/auth/feature-icon-google-checked.png`;

interface LoginWithSsoModalProps {
  signinMethod: SSOAuthProviderKey;
  show: boolean;
  onClose: Function;
  onUserLoggedIn: Function;
}

const LoginWithSsoModal: React.FC<LoginWithSsoModalProps> = ({
  signinMethod,
  show,
  onClose,
  onUserLoggedIn,
}) => {
  // #region HOOKS
  const language = useAppLanguage();
  const dispatch = useDispatch();
  // #endregion

  // #region TRANSLATIONS
  const viewMessages = messages[language].loginWithSso;
  // #endregion

  // #region FUNCTIONS
  const getGraphics = () => {
    switch (signinMethod) {
      case 'GOOGLE':
        return {
          image: imgGoogleFeatureIcon,
          icon: imgGoogleIcon,
        };
      case 'APPLE':
        return {
          image: imgAppleFeatureIcon,
          icon: imgAppleIcon,
        };
      default:
        return {
          image: imgUser,
          icon: null,
        };
    }
  };
  // #endregion

  // #region MISC
  const loginMethods: LoginMethodOption[] = [
    {
      key: 'GOOGLE',
      name: viewMessages.google,
    },
    {
      key: 'APPLE',
      name: viewMessages.apple,
    },
  ];

  const selectedMethod = loginMethods.find(
    (lm: LoginMethodOption) => lm.key === signinMethod,
  );
  // #endregion

  // #region HANDLERS
  const handleSsoLogin = () => {
    loginWith(
      signinMethod,
      (response: any) => {
        dispatch(
          loginWithProviderAction(
            {
              provider: signinMethod,
              clientApplicationSsoCode: `DASHBOARD_${signinMethod}`,
              ssoJwt: response?.user?.accessToken,
            },
            (res: any) => {
              if (res.success) {
                onUserLoggedIn();
              }
            },
          ),
        );
      },
      (error: any) => {
        console.log('ERROR', error);
        // TODO: error handling
      },
    );
  };
  // #endregion

  return (
    <Modal
      show={show}
      hasBlurredBackground
      closeModal={onClose}
      content={
        <div className="text-center">
          <div className="text-header-text text-2xl">{viewMessages.title}</div>
          <img
            src={getGraphics().image}
            alt={`login-method-${selectedMethod?.key}`}
            className="w-24 my-4 mx-auto"
          />
          <div />
          <div
            dangerouslySetInnerHTML={{
              __html: viewMessages.description(selectedMethod?.name as string),
            }}
          />
          <div className="flex flex-col items-center justify-center">
            <Button className="w-2/3 my-2" onClick={handleSsoLogin}>
              {getGraphics()?.icon && (
                <img src={getGraphics()?.icon as string} alt="sso-provider" />
              )}
              &nbsp;
              {viewMessages.submitText(toSentenceCase(signinMethod as string))}
            </Button>
            <Button className="w-2/3" outlined onClick={() => onClose?.()}>
              {viewMessages.secondaryText}
            </Button>
          </div>
        </div>
      }
      hideModal={onClose}
    />
  );
};

export default LoginWithSsoModal;
